// @flow
// $FlowFixMe - Cannot import useState because there is no useState export in react.
import React, { useEffect } from 'react';
import {
  SidebarSection,
  TextInputHookForm,
  Text,
  SelectHookForm,
  Checkbox,
  checkboxStates,
  TextareaHookForm,
} from 'bench-internal-theme';
import { colors } from 'bench-style-constants';
import { useFormContext } from 'react-hook-form';
import { FeatureValue, useFeatureFlags } from '@bench/ff-client';
// $FlowFixMe - TS file
import { SplitFeatureFlags } from '../../features';
import { categoryOptions } from './formOptions';
import CurrencySelect from './CurrencySelect';
// $FlowFixMe - TSX file
import DisplayGroupSelect from './DisplayGroupSelect';
import { FormGroup, CheckboxGroup } from '../FormGroups';
import type { LedgerAccount } from '../types';

type LedgerAccountDetailsSidebarSectionProps = {
  isFunctional: boolean,
  isSubmitting: boolean,
  isTemporary: boolean,
  ledgerAccount: ?LedgerAccount,
  setIsFunctional: boolean => void,
  setIsTemporary: boolean => void,
};

const LedgerAccountDetailsSidebarSection = ({
  isFunctional,
  isSubmitting,
  isTemporary,
  ledgerAccount,
  setIsFunctional,
  setIsTemporary,
}: LedgerAccountDetailsSidebarSectionProps) => {
  const { watch, setValue } = useFormContext();

  const [featureState] = useFeatureFlags();
  const {
    featureContext: { features },
  } = featureState;
  const isDisplayGroupEnabled =
    features[SplitFeatureFlags.ASSIGN_CATEGORY_IMPROVEMENT]?.value === FeatureValue.FEATURE_ON;
  const isPBLedgerAccount = ledgerAccount?.category?.id === '9';
  // If the id is defined, we are editing an existing ledger. We do not want to show the PB category option if we are editing.
  // Category select is disable if isPBLedgerAccount is true; but, the category value will not show in the disabled component
  // if we don't make the isPBLedgerAccount exception here.
  const showPBCategoryInCategorySelect = !ledgerAccount?.id || isPBLedgerAccount;

  const categoryId = watch('categoryId');
  if (!isFunctional && categoryId?.value === '9') setIsFunctional(true);

  useEffect(() => {
    const details = watch('details');
    const payrollAutomationDetails = 'Use for automation with payroll or invoicing providers';
    const isNonFunctionalCategory = ['6', '7', '8'].includes(categoryId?.value);
    if (isNonFunctionalCategory && !ledgerAccount?.id && isFunctional && !details && categoryId) {
      setValue('details', payrollAutomationDetails);
    } else if ((!isNonFunctionalCategory || !isFunctional) && details === payrollAutomationDetails)
      setValue('details', null);
    // We do not need to update if the user is inputting their own details
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledgerAccount, isFunctional, categoryId]);

  const isFunctionalDisabled = () => {
    if (!!ledgerAccount) {
      if (ledgerAccount.role) return false;
      return ledgerAccount.accountType !== 'FLAVOUR';
    }

    return false;
  };

  return (
    <>
      <SidebarSection>
        <FormGroup>
          <Text color={colors.cloud} styleType="small" uppercase>
            Name
          </Text>
          <TextInputHookForm
            aria-label="Name"
            defaultValue={ledgerAccount?.name}
            disabled={isSubmitting}
            name="name"
            rules={{ required: { value: true, message: 'Name is required' } }}
            type="text"
          />
        </FormGroup>
      </SidebarSection>
      <SidebarSection title="Notes" background={colors.shell}>
        <TextareaHookForm
          aria-label="Notes"
          defaultValue={ledgerAccount?.details}
          disabled={isSubmitting}
          name="details"
          rules={{ maxLength: { value: 1000, message: 'Notes cannot exceed 1000 characters' } }}
          type="text"
        />
      </SidebarSection>
      <SidebarSection>
        <FormGroup>
          <Text color={colors.cloud} styleType="small" uppercase>
            Currency
          </Text>
          <CurrencySelect
            currencyId={ledgerAccount?.currencyId}
            rules={{ required: { value: true, message: 'Currency is required' } }}
          />
          <Text color={colors.cloud} styleType="small" uppercase>
            Category
          </Text>
          <SelectHookForm
            aria-label="Category"
            defaultValue={ledgerAccount?.category?.id || '1'}
            isDisabled={isSubmitting || isPBLedgerAccount}
            name="categoryId"
            options={categoryOptions(showPBCategoryInCategorySelect)}
          />
          {isDisplayGroupEnabled && !isFunctional && (
            <>
              <Text color={colors.cloud} styleType="small" uppercase>
                Display Group
              </Text>
              <DisplayGroupSelect ledgerAccount={ledgerAccount} isSubmitting={isSubmitting} />
            </>
          )}
        </FormGroup>
        {categoryId?.value !== '9' && (
          <CheckboxGroup>
            <Checkbox
              aria-label="Is Temporary"
              disabled={isSubmitting}
              checkboxState={isTemporary ? checkboxStates.CHECKED : checkboxStates.UNCHECKED}
              onClick={() => setIsTemporary(!isTemporary)}
            />
            <Text color={colors.cloud} styleType="small" uppercase>
              Temporary
            </Text>
            <Checkbox
              aria-label="Is Functional"
              disabled={isSubmitting || isFunctionalDisabled()}
              onClick={() => setIsFunctional(!isFunctional)}
              checkboxState={isFunctional ? checkboxStates.CHECKED : checkboxStates.UNCHECKED}
            />
            <Text
              color={isFunctionalDisabled() ? colors.stone : colors.cloud}
              styleType="small"
              uppercase
            >
              Functional Ledger
            </Text>
          </CheckboxGroup>
        )}
      </SidebarSection>
    </>
  );
};

export default LedgerAccountDetailsSidebarSection;
